import { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  OTP_CANCEL_BTN,
  OTP_RESEND_BTN,
  OTP_RESEND_WHATSAPP_BTN,
  OTP_SEND_BTN,
  OTP_VERIFIED_BTN,
} from "../../../../AppConstants/AnalyticsEventName";
import {
  getOTPSendData,
  getOTPVerificationData,
} from "../../../../Clevertap/EventHandler.ts";
import {
  OTP_RESEND_EVENT,
  OTP_SEND_EVENT,
  OTP_VERIFY_EVENT,
} from "../../../../Clevertap/EventNames.ts";
import { usersAPI } from "../../../../apis/usersAPI.js";
import LoaderImg from "../../../../assets/images/Loader.gif";
import BackBtnImg from "../../../../assets/images/back.png";
import closebtnImg from "../../../../assets/images/close.png";
import { AnalyticsContext } from "../../../../contexts/Analytics/AnalyticsContextProvider";
import { authContext } from "../../../../contexts/AuthContext/AuthContextProvider";
import { AuthManager } from "../../../../firebase/firebase";
import { windowOpenUrl } from "../../../../utils/common.utils";
import styles from "./OTPForm.module.scss";
import whatsapp from "../../../../assets/images/whatsapp.png";
import whatsappGreen from "../../../../assets/images/whatsappGreen.png";
import smsImg from "../../../../assets/images/sms.png";
import { getOTPDeliveryMethod } from "../../../../config/otp.config.ts";
import { OTPDeliveryMethod } from "../../../../types/otp.types.ts";
import { ConstantDataContext } from "../../../../contexts/ConstantContext/ConstantDataContextProvider.js";
import { UserContext } from "../../../../contexts/UserContext/UserContextProvider.js";

// OTP storage utilities consolidated in one place
const OTPStorage = {
  updateSendCount: () => {
    const prevCount =
      Number.parseInt(localStorage.getItem("send_otp_count") || "0") + 1;
    localStorage.setItem("send_otp_count", prevCount.toString());
    return prevCount;
  },

  getResendInfo: () => ({
    resendCount: localStorage.getItem("resend_count"),
    lastResendTime: localStorage.getItem("last_resend_time"),
    resendRemainingSec: localStorage.getItem("resend_remaining_sec"),
  }),

  updateResendInfo: () => {
    const resendCount =
      Number.parseInt(localStorage.getItem("resend_count") || "0") + 1;
    localStorage.setItem("resend_count", resendCount.toString());

    const currentTime = new Date().getTime();
    const lastResendTime =
      Number.parseInt(
        localStorage.getItem("last_resend_time") || currentTime.toString()
      ) +
      30 * 1000;
    localStorage.setItem("last_resend_time", lastResendTime.toString());

    return { resendCount, lastResendTime };
  },

  clearResendTimer: () => {
    localStorage.removeItem("resend_remaining_sec");
  },
};

const OTPForm = () => {
  const {
    toggleSignInbtn,
    state,
    handleLoading,
    toggleSignUpScreens,
    handleFormDetails,
    resetState,
  } = useContext(authContext);
  const { addGAWithUserInfo, addCleverTapAnalytics, addGAWithNoUser } =
    useContext(AnalyticsContext);
  const { anonymousUser, setAnonymousUser } = useContext(UserContext);
  const { remoteConfigData } = useContext(ConstantDataContext);
  const { formDetails, loading } = state;
  const { marketing, phoneNumber, terms } = formDetails;

  // Consolidated state
  const [otpState, setOtpState] = useState({
    digit1: "",
    digit2: "",
    digit3: "",
    digit4: "",
  });
  const [uiState, setUiState] = useState({
    error: "",
    otpSent: false,
    resendLoading: false,
    resendType: "",
    remainingTime: 0,
    resendBtnStatus: false,
  });

  const resendInterval = useRef(null);
  const userNumber = anonymousUser?.phone;
  const maxOTPLength = 4;
  const OTPDigits = Object.values(otpState).join("");
  const otpBtnDisabled = OTPDigits.length < maxOTPLength || !terms || loading;

  // Get delivery method based on phone number
  const deliveryMethod = getOTPDeliveryMethod(
    remoteConfigData?.otp,
    userNumber
  );
  const primaryMethod = deliveryMethod?.primary_method || OTPDeliveryMethod.SMS;
  const resendOptions = deliveryMethod?.resend_options || [
    OTPDeliveryMethod.SMS,
  ];

  // Helper to update UI state partially
  const updateUiState = (updates) => {
    setUiState((prev) => ({ ...prev, ...updates }));
  };

  // Timer management
  const startResendTimer = (sec = 30) => {
    updateUiState({ remainingTime: sec, resendBtnStatus: true });

    // Clear any existing interval
    if (resendInterval.current) {
      clearInterval(resendInterval.current);
    }

    resendInterval.current = setInterval(() => {
      setUiState((prev) => {
        if (prev.remainingTime > 1) {
          const newTime = prev.remainingTime - 1;
          localStorage.setItem("resend_remaining_sec", newTime.toString());
          return { ...prev, remainingTime: newTime };
        } else {
          clearInterval(resendInterval.current);
          OTPStorage.clearResendTimer();
          return { ...prev, remainingTime: 0, resendBtnStatus: false };
        }
      });
    }, 1000);
  };

  // Initialize timer if needed
  useEffect(() => {
    startResendTimer();

    // Check if there's a remaining time from previous session
    const { resendRemainingSec } = OTPStorage.getResendInfo();
    if (resendRemainingSec && Number.parseInt(resendRemainingSec) > 0) {
      startResendTimer(Number.parseInt(resendRemainingSec));
    }

    // Cleanup
    return () => {
      if (resendInterval.current) {
        clearInterval(resendInterval.current);
      }
    };
  }, []);

  // Modified sendOTP function to use dynamic configuration
  const sendOTP = async (type = primaryMethod) => {
    // If type is not in resendOptions, use primary method
    if (!resendOptions.includes(type)) {
      type = primaryMethod;
    }
    handleLoading(true);
    const isResend = uiState.otpSent;
    const actionType = isResend
      ? type === OTPDeliveryMethod.SMS
        ? OTP_RESEND_BTN
        : OTP_RESEND_WHATSAPP_BTN
      : OTP_SEND_BTN;

    updateUiState({ resendLoading: true, error: "", resendType: type });

    const extraData = { Status: false, message: "" };

    try {
      // Choose the appropriate API method
      const apiMethod =
        type === OTPDeliveryMethod.SMS
          ? usersAPI.sendOTP
          : usersAPI.sendWhatsAppOTP;
      let res = await apiMethod({
        phone: anonymousUser.phone,
      });

      // Update counts and status
      const retries = isResend
        ? OTPStorage.updateResendInfo().resendCount
        : OTPStorage.updateSendCount();

      extraData.Status = true;

      if (!isResend) {
        handleLoading(false);
      }

      updateUiState({
        otpSent: true,
        resendLoading: false,
      });
      handleLoading(false);
      startResendTimer();
    } catch (error) {
      const { data } = error;
      const { response } = data;
      const errorMessage =
        response?.details || error?.data?.message || "Error in processing";

      extraData.Status = false;
      extraData.message = errorMessage;

      //   if (!isResend) {
      handleLoading(false);
      //   }

      updateUiState({
        error: errorMessage,
        resendLoading: false,
      });
    } finally {
      const eventName = isResend ? OTP_RESEND_EVENT : OTP_SEND_EVENT;
      const retryCount = isResend
        ? localStorage.getItem("resend_count")
        : localStorage.getItem("send_otp_count");

      addCleverTapAnalytics(
        eventName,
        getOTPSendData({
          Medium: `${type} OTP`,
          Retries: retryCount,
          Status: extraData.Status,
          "Status Message": extraData.message,
        })
      );
      addGAWithNoUser(actionType, {
        Medium: `${type} OTP`,
        Retries: retryCount,
        Status: extraData.Status,
        phoneNumber: anonymousUser.phone,
        Status_Message: extraData.message,
      });
    }
  };

  // OTP input handling with improved focus management and auto-submit
  const handleOTPchange = (e) => {
    const { value, name } = e.target;
    const [_, fieldIndex] = name.split("-");

    // Only accept single digits
    if (value.length > 1) return;

    // Update the OTP state
    setOtpState((prev) => ({
      ...prev,
      [`digit${fieldIndex}`]: value,
    }));

    // Clear error if any
    if (uiState.error) {
      updateUiState({ error: "" });
    }

    // Auto-focus next field if value is entered
    if (value && Number.parseInt(fieldIndex, 10) < maxOTPLength) {
      const nextInput = document.querySelector(
        `input[name=digit-${Number.parseInt(fieldIndex, 10) + 1}]`
      );
      if (nextInput) {
        nextInput.focus();
      }
    }

    // Check if all digits are filled and auto-submit
    const updatedOTP = {
      ...otpState,
      [`digit${fieldIndex}`]: value,
    };
    const allDigitsFilled = Object.values(updatedOTP).every(
      (digit) => digit.length === 1
    );
    if (allDigitsFilled) {
      handleSubmit(null, Object.values(updatedOTP).join(""));
    }
  };

  // Handle backspace for OTP fields
  const handleBackErase = (e) => {
    if (e.key !== "Backspace") return;

    const { name, value } = e.target;
    const [_, fieldIndex] = name.split("-");

    // If current field is empty and not the first field, focus previous field
    if (value.length === 0 && Number.parseInt(fieldIndex, 10) > 1) {
      const prevInput = document.querySelector(
        `input[name=digit-${Number.parseInt(fieldIndex, 10) - 1}]`
      );
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  // OTP verification
  const handleSubmit = async (e, otp) => {
    if (e) {
      e.preventDefault();
    }
    if (!uiState.otpSent) {
      return;
    }
    let _otpString = otp || OTPDigits;
    if (_otpString.length < maxOTPLength) {
      updateUiState({ error: "Please enter the complete OTP" });
      return;
    }

    const extraData = { Status: false, message: "" };
    handleLoading(true);

    try {
      const apiMethod =
        uiState.resendType === OTPDeliveryMethod.SMS
          ? usersAPI.verifyOTP
          : usersAPI.verifyWhatsAppOTP;

      let verifyRes = await apiMethod({
        otp: _otpString,
        marketing: marketing,
        phone: anonymousUser.phone,
      });
      if (
        verifyRes.status === 200 &&
        verifyRes.data.response.status === "success"
      ) {
        let extractedToken = verifyRes.data.token;
        await AuthManager.loginWithCustomToken(extractedToken);
        toggleSignInbtn("showOTPModal", false);
        setAnonymousUser(null);
      }

      extraData.Status = true;
      addGAWithNoUser(OTP_VERIFIED_BTN, {
        success: true,
        phoneNumber: anonymousUser.phone,
      });
      // Handle successful verification
      toggleSignInbtn("showOTPModal", false);
      setAnonymousUser(null);
    } catch (error) {
      console.log("error :: ", error);

      const { data } = error;
      const { message } = data;

      extraData.Status = false;
      extraData.message = message;

      addGAWithNoUser(OTP_VERIFIED_BTN, {
        success: false,
        phoneNumber: anonymousUser.phone,
      });
      updateUiState({ error: message });
    } finally {
      handleLoading(false);
      addCleverTapAnalytics(
        OTP_VERIFY_EVENT,
        getOTPVerificationData({
          Medium:
            uiState.resendType === OTPDeliveryMethod.SMS
              ? "SMS OTP"
              : "WHATSAPP OTP",
          Status: extraData.Status,
          "Status Message": extraData.message,
        })
      );
    }
  };

  // Cancel button handler
  const handleCancelBtn = () => {
    addGAWithNoUser(OTP_CANCEL_BTN, { phoneNumber: anonymousUser.phone });
    // AuthManager.logout();
    toggleSignInbtn("showOTPModal", false);
    resetState();
    setAnonymousUser(null);
  };

  return (
    <form onSubmit={handleSubmit} className={styles["otp-form"]}>
      {uiState.otpSent ? (
        <div>
          <h1>Verify Mobile</h1>
          <p className={styles["number-text"]}>
            Please enter OTP for verifying your number
            <br />
            <span>{userNumber}</span>
          </p>
          <div className={styles["otp-container"]}>
            {[1, 2, 3, 4].map((index) => (
              <input
                disabled={loading}
                key={`digit-${index}`}
                value={otpState[`digit${index}`]}
                name={`digit-${index}`}
                onChange={handleOTPchange}
                onKeyDown={handleBackErase}
                type="number"
                min={0}
                max={9}
                maxLength={1}
                autoFocus={index === 1}
              />
            ))}
          </div>

          <div className={styles["terms"]}>
            <label className={"custom-checkbox"}>
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => handleFormDetails({ terms: e.target.checked })}
              />
            </label>
            <p>
              I accept{" "}
              <Link to={"/terms-of-use"} target="_blank">
                <span>Terms of use</span>{" "}
              </Link>{" "}
              and{" "}
              <span
                onClick={() =>
                  windowOpenUrl("https://www.mankindpharma.com/privacy-policy")
                }
              >
                Privacy Policy
              </span>
              .
            </p>
          </div>
          {/* <div className={styles["terms"]} style={{ margin: 0 }}>
            <label className={"custom-checkbox"}>
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) =>
                  handleFormDetails({ marketing: e.target.checked })
                }
              />
            </label>
            <p>I don't want to receive marketing messages from Docflix.</p>
          </div> */}

          {uiState.error && <div className="error">{uiState.error}</div>}

          <div className={styles.actionBtns}>
            <h3>Didn't Receive OTP?</h3>
            <div className={styles.resendButtonContainer}>
              {resendOptions.includes(OTPDeliveryMethod.SMS) && (
                <div
                  className={`${styles["resend-btn"]} ${
                    uiState.resendLoading || uiState.resendBtnStatus || loading
                      ? styles.disabled
                      : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (
                      !uiState.resendLoading &&
                      !uiState.resendBtnStatus &&
                      !loading
                    ) {
                      sendOTP(OTPDeliveryMethod.SMS);
                    }
                  }}
                >
                  <img className={styles.icon} src={smsImg} alt="" />
                  {uiState.resendLoading &&
                  uiState.resendType === OTPDeliveryMethod.SMS
                    ? "Sending..."
                    : `Resend via SMS ${
                        uiState.remainingTime > 0 &&
                        uiState.resendType === OTPDeliveryMethod.SMS
                          ? "(" +
                            String(uiState.remainingTime).padStart(2, "0") +
                            ")"
                          : ""
                      }`}
                </div>
              )}

              {resendOptions.includes(OTPDeliveryMethod.WHATSAPP) && (
                <div
                  className={`${styles["resend-btn"]} ${
                    uiState.resendLoading || uiState.resendBtnStatus || loading
                      ? styles.disabled
                      : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (
                      !uiState.resendLoading &&
                      !uiState.resendBtnStatus &&
                      !loading
                    ) {
                      sendOTP(OTPDeliveryMethod.WHATSAPP);
                    }
                  }}
                >
                  <img
                    className={styles.icon}
                    src={
                      uiState.resendLoading ||
                      uiState.resendBtnStatus ||
                      loading
                        ? whatsappGreen
                        : whatsapp
                    }
                    alt=""
                  />
                  {uiState.resendLoading &&
                  uiState.resendType === OTPDeliveryMethod.WHATSAPP
                    ? "Sending..."
                    : `Resend via WhatsApp ${
                        uiState.remainingTime > 0 &&
                        uiState.resendType === OTPDeliveryMethod.WHATSAPP
                          ? "(" +
                            String(uiState.remainingTime).padStart(2, "0") +
                            ")"
                          : ""
                      }`}
                </div>
              )}
            </div>

            <button
              disabled={otpBtnDisabled}
              className={`btn ${styles["next-btn"]}`}
            >
              {loading ? (
                <img src={LoaderImg} alt="Loading" height="100%" />
              ) : (
                "Submit"
              )}
            </button>
          </div>
          <div onClick={handleCancelBtn} className={styles["closeBtn"]}>
            <img src={closebtnImg} alt="close" />
          </div>
        </div>
      ) : (
        <div>
          <p className={styles["number-text"]}>
            We will send you the verification code to{" "}
            <span>{anonymousUser?.phone}</span>
          </p>
          {uiState.error && <div className="error">{uiState.error}</div>}
          <div className={`${styles.actionBtns} ${styles.actionBtns2}`}>
            <button
              disabled={loading}
              onClick={() => sendOTP(OTPDeliveryMethod.SMS)}
              className={`btn ${styles["send-otp-btn"]}`}
            >
              {loading ? (
                <img src={LoaderImg} alt="Loading" height="100%" />
              ) : (
                "Send OTP"
              )}
            </button>
            <button
              disabled={loading}
              onClick={handleCancelBtn}
              className={`btn ${styles["cancel-btn"]}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

export default OTPForm;
